'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, Phone, Mail, Upload, Shield } from 'lucide-react';
import { toast } from 'sonner';
import OTPModal from './OTPModal';

interface HMFormData {
  fullName: string;
  username: string;
  email: string;
  phone1: string;
  phone2: string;
  homeAddress: string;
  country: string;
  bio: string;
  password: string;
  confirmPassword: string;
}

interface HomeManagerRegisterFormProps {
  onSwitchToLogin: () => void;
}

const countries = [
  'Zimbabwe', 'South Africa', 'Botswana', 'Zambia', 'Mozambique',
  'Namibia', 'Malawi', 'Tanzania', 'Kenya', 'United Kingdom',
];

const inputClass = "w-full px-4 py-2.5 text-sm bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary transition-all placeholder:text-slate-400 text-slate-800";
const inputStyle = { borderColor: 'rgba(232,93,4,0.20)' };
const selectClass = "w-full px-4 py-2.5 text-sm bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary transition-all appearance-none text-slate-800";

export default function HomeManagerRegisterForm({ onSwitchToLogin }: HomeManagerRegisterFormProps) {
  const router = useRouter();
  const [registrationType, setRegistrationType] = useState<'email' | 'phone'>('email');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profilePic, setProfilePic] = useState<string | null>(null);
  const [showOTP, setShowOTP] = useState(false);
  const [pendingPhone, setPendingPhone] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    formState: { errors },
  } = useForm<HMFormData>();

  const password = watch('password');

  const handleProfilePicChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setProfilePic(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data: HMFormData) => {
    if (registrationType === 'phone') {
      setPendingPhone(data.phone1);
      setShowOTP(true);
      return;
    }
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1600));
    toast.success('Account created! Redirecting to complete your profile...');
    router.push('/home-manager-profile');
  };

  const handleOTPVerified = async () => {
    setShowOTP(false);
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1000));
    toast.success('Phone verified! Account created successfully.');
    router.push('/home-manager-profile');
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Registration type toggle */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Register using</label>
          <div className="flex rounded-xl overflow-hidden" style={{border: '1px solid rgba(232,93,4,0.20)'}}>
            <button
              type="button"
              onClick={() => setRegistrationType('email')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-all ${
                registrationType === 'email' ? 'text-primary' : 'bg-white text-slate-500 hover:bg-orange-50'
              }`}
              style={registrationType === 'email' ? {background: 'rgba(232,93,4,0.08)'} : {}}
            >
              <Mail size={15} />
              Email Address
            </button>
            <button
              type="button"
              onClick={() => setRegistrationType('phone')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-all ${
                registrationType === 'phone' ? 'text-primary' : 'bg-white text-slate-500 hover:bg-orange-50'
              }`}
              style={registrationType === 'phone' ? {background: 'rgba(232,93,4,0.08)'} : {}}
            >
              <Phone size={15} />
              Phone Number
            </button>
          </div>
          {registrationType === 'phone' && (
            <div className="mt-2 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <Shield size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700">An OTP will be sent to your phone number to verify your identity. Standard SMS rates may apply.</p>
            </div>
          )}
        </div>

        {/* Profile picture */}
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0" style={{background: 'rgba(232,93,4,0.06)', border: '2px dashed rgba(232,93,4,0.25)'}}>
            {profilePic ? (
              <img src={profilePic} alt="Profile preview" className="w-full h-full object-cover" />
            ) : (
              <Upload size={20} className="text-primary/50" />
            )}
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">
              Profile Picture <span className="text-slate-400 font-normal">(Optional)</span>
            </label>
            <label className="cursor-pointer inline-block px-3 py-1.5 bg-white text-xs font-medium text-slate-700 hover:bg-orange-50 transition-all rounded-lg" style={{border: '1px solid rgba(232,93,4,0.20)'}}>
              Upload Photo
              <input type="file" accept="image/*" className="hidden" onChange={handleProfilePicChange} />
            </label>
          </div>
        </div>

        {/* Full Name */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Full Name <span className="text-red-500">*</span></label>
          <input type="text" {...register('fullName', { required: 'Full name is required' })} placeholder="e.g. Zanele Dube" className={inputClass} style={inputStyle} />
          {errors.fullName && <p className="mt-1 text-xs text-red-600">{errors.fullName.message}</p>}
        </div>

        {/* Username */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Username <span className="text-red-500">*</span></label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">@</span>
            <input type="text" {...register('username', { required: 'Username is required', pattern: { value: /^[a-z0-9_]+$/, message: 'Lowercase letters, numbers, and underscores only' } })} placeholder="zanele_dube" className={`${inputClass} pl-8`} style={inputStyle} />
          </div>
          {errors.username && <p className="mt-1 text-xs text-red-600">{errors.username.message}</p>}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">
            Email Address{' '}
            {registrationType === 'email' ? <span className="text-red-500">*</span> : <span className="text-slate-400 font-normal">(Optional)</span>}
          </label>
          <input type="email" {...register('email', { required: registrationType === 'email' ? 'Email is required for email registration' : false, pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' } })} placeholder="zanele@email.com" className={inputClass} style={inputStyle} />
          {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
        </div>

        {/* Phone numbers */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Phone 1 <span className="text-red-500">*</span></label>
            <input type="tel" {...register('phone1', { required: 'Primary phone is required' })} placeholder="+263 77 123 4567" className={inputClass} style={inputStyle} />
            {errors.phone1 && <p className="mt-1 text-xs text-red-600">{errors.phone1.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Phone 2 <span className="text-slate-400 font-normal">(Opt.)</span></label>
            <input type="tel" {...register('phone2')} placeholder="+263 71 987 6543" className={inputClass} style={inputStyle} />
          </div>
        </div>

        {/* Home Address */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Home Address <span className="text-red-500">*</span></label>
          <p className="text-xs text-slate-400 mb-1.5">Your residential address (suburb, city)</p>
          <input type="text" {...register('homeAddress', { required: 'Home address is required' })} placeholder="e.g. 14 Jacaranda Ave, Borrowdale, Harare" className={inputClass} style={inputStyle} />
          {errors.homeAddress && <p className="mt-1 text-xs text-red-600">{errors.homeAddress.message}</p>}
        </div>

        {/* Country */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Country <span className="text-red-500">*</span></label>
          <select {...register('country', { required: 'Country is required' })} className={selectClass} style={inputStyle}>
            <option value="">Select country</option>
            {countries.map((c) => <option key={`country-hm-${c}`} value={c}>{c}</option>)}
          </select>
          {errors.country && <p className="mt-1 text-xs text-red-600">{errors.country.message}</p>}
        </div>

        {/* Bio */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">About Me <span className="text-slate-400 font-normal">(Optional)</span></label>
          <textarea {...register('bio')} rows={3} placeholder="e.g. Experienced Home Manager with 8 years of experience in childcare, cooking, and housekeeping..." className="w-full px-4 py-2.5 text-sm bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary transition-all resize-none placeholder:text-slate-400 text-slate-800" style={inputStyle} />
        </div>

        {/* Password fields */}
        {registrationType === 'email' && (
          <>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Password <span className="text-red-500">*</span></label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} {...register('password', { required: registrationType === 'email' ? 'Password is required' : false, minLength: { value: 8, message: 'Password must be at least 8 characters' } })} placeholder="Min. 8 characters" className={`${inputClass} pr-11`} style={inputStyle} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Confirm Password <span className="text-red-500">*</span></label>
              <div className="relative">
                <input type={showConfirm ? 'text' : 'password'} {...register('confirmPassword', { required: 'Please confirm your password', validate: (v) => v === password || 'Passwords do not match' })} placeholder="Re-enter your password" className={`${inputClass} pr-11`} style={inputStyle} />
                <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.confirmPassword && <p className="mt-1 text-xs text-red-600">{errors.confirmPassword.message}</p>}
            </div>
          </>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 text-white text-sm font-bold rounded-xl hover:opacity-90 active:scale-[0.98] transition-all duration-150 disabled:opacity-60 flex items-center justify-center gap-2"
          style={{ minHeight: '44px', background: 'linear-gradient(135deg, #F47B20 0%, #e8650a 100%)', boxShadow: '0 4px 14px rgba(232,93,4,0.30)' }}
        >
          {loading ? (
            <><Loader2 size={16} className="animate-spin" /> Creating account...</>
          ) : registrationType === 'phone' ? (
            'Send OTP & Register'
          ) : (
            'Create Home Manager Account'
          )}
        </button>

        <p className="text-center text-sm text-slate-500">
          Already registered?{' '}
          <button type="button" onClick={onSwitchToLogin} className="text-primary font-semibold hover:underline">Sign in</button>
        </p>
      </form>

      {showOTP && (
        <OTPModal
          phone={pendingPhone}
          onVerified={handleOTPVerified}
          onClose={() => setShowOTP(false)}
        />
      )}
    </>
  );
}